import type { ReactNode } from 'react';

import { getIn } from 'formik';
import type { AnyAction, Dispatch } from 'redux';
import styled from 'styled-components';

import AddressBuilder from './AddressBuilder';
import ToggleAddress from './ToggleAddress';
import type { AddressFieldConfig, ApiServiceLike, FormikLike, StoreLike } from './types';

const Wrapper = styled.div`
  align-self: flex-start;
  width: 100%;
`;

interface FieldAddressProps {
  /** Name of address property in the store. */
  name: string;
  /** Redux store slice containing the customer. */
  store: StoreLike;
  /** Triggers reducers and updates the store based on action. */
  dispatch: Dispatch<AnyAction>;
  /** Formik object to handle form validation and submission. */
  formik?: FormikLike;
  /** Service used to persist customer changes. */
  apiService?: ApiServiceLike;
  /** Address field configurations. */
  addressFields?: AddressFieldConfig[];
  /** Renders sold-to toggle option when true. */
  showSoldTo?: boolean;
  /** Renders bill-to toggle option when true. */
  showBillTo?: boolean;
  /** Renders payer toggle option when true. */
  showPayer?: boolean;
  /** Hides the "same as" toggle group when true. */
  hideSameAs?: boolean;
  /** Nodes that you want to render. */
  children?: ReactNode;
}

function FieldAddress(props: FieldAddressProps) {
  const {
    addressFields = [],
    showSoldTo,
    showBillTo,
    showPayer,
    children,
    name,
    store,
    hideSameAs,
    ...restProps
  } = props;

  // Get useSoldTo, useBillTo, usePayer from the address in the store
  const useSoldTo = getIn(store.customer, `${name}.useSoldTo`);
  const useBillTo = getIn(store.customer, `${name}.useBillTo`);
  const usePayer = getIn(store.customer, `${name}.usePayer`);

  // Varaible use to toggle AddressBuilder and show toggle button as active when all properties are false
  const isDifferent = !useBillTo && !useSoldTo && !usePayer;
  const updatedAddressFileds: AddressFieldConfig[] =
    addressFields.length > 0
      ? addressFields.map(item => ({
          required: item.required ? isDifferent && item.require : false,
          ...item
        }))
      : addressFields;
  const toggleProps = { name, showBillTo, showPayer, store, ...restProps };

  return (
    <Wrapper>
      {(showSoldTo || showBillTo || showPayer) && !hideSameAs && <ToggleAddress {...toggleProps} />}
      {children}
      {isDifferent && <AddressBuilder name={name} addressFields={updatedAddressFileds} />}
    </Wrapper>
  );
}

export default FieldAddress;
